import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { FEATURED, TIMELINE, META, PRESS } from './data';

// HOME — type-driven editorial. NO hero image. Asymmetric grid.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#F2EBDA] text-[#0E0E0E]">
      {/* Hero: massive type left, single product right. NO background image. */}
      <section className="max-w-[1440px] mx-auto px-8 lg:px-12 pt-16 pb-24 grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-20 items-end border-b border-[#0E0E0E]/10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-6">— Geneva &middot; London &middot; Singapore &middot; est. {META.established}</p>
          <h1 className="font-serif text-[64px] sm:text-[88px] lg:text-[148px] leading-[0.85] tracking-[-0.02em]">
            A few <span className="italic text-[#7B2D26]">honest</span><br />watches,<br />kept <span className="italic">well</span>.
          </h1>
          <p className="text-base lg:text-lg text-[#0E0E0E]/65 leading-[1.7] mt-10 max-w-md">A small horology house. We sell what we would wear, restore what we would keep, and decline what we would not. Fifty years on the same street.</p>
          <div className="flex flex-wrap items-center gap-8 mt-10">
            <button onClick={() => setPage('collection')} className="bg-[#0E0E0E] text-[#F2EBDA] px-8 py-3.5 font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-[#7B2D26] transition-colors duration-500" data-testid="watch-home-collection-cta">Browse the collection</button>
            <button onClick={() => setPage('visit')} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#0E0E0E]/70 hover:text-[#7B2D26] flex items-center gap-1 group">
              Book a private viewing <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </motion.div>

        {/* Single watch card — feature piece */}
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }} className="border border-[#0E0E0E]/15 bg-[#F8F2E2] p-6">
          <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-[#7B2D26] mb-4">— Piece of the week</p>
          <div className="aspect-square overflow-hidden bg-[#F2EBDA] mb-4 p-6"><img src={FEATURED[0].img} alt={FEATURED[0].name} className="w-full h-full object-cover" loading="lazy" /></div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0E0E0E]/50 mb-1">Ref. {FEATURED[0].ref} &middot; {FEATURED[0].year}</p>
          <p className="font-serif text-xl leading-tight" dangerouslySetInnerHTML={{ __html: FEATURED[0].name }} />
          <div className="border-t border-[#0E0E0E]/10 mt-4 pt-4 flex items-end justify-between">
            <p className="font-mono text-[11px] text-[#0E0E0E]/55">CHF</p>
            <p className="font-serif text-2xl text-[#7B2D26]">{FEATURED[0].price}</p>
          </div>
        </motion.div>
      </section>

      {/* Press — quiet single line, no marquee */}
      <section className="border-b border-[#0E0E0E]/10 py-8">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12">
          <div className="flex flex-wrap items-center gap-x-12 gap-y-3 font-mono text-[11px] tracking-[0.25em] uppercase text-[#0E0E0E]/40">
            <span>Featured in &mdash;</span>
            {PRESS.map(p => <span key={p} className="hover:text-[#0E0E0E] transition-colors cursor-default" dangerouslySetInnerHTML={{ __html: p }} />)}
          </div>
        </div>
      </section>

      {/* Featured 3 pieces — asymmetric: 1 large left, 2 stacked right */}
      <section className="max-w-[1440px] mx-auto px-8 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-serif text-4xl lg:text-6xl leading-tight">Recently <span className="italic text-[#7B2D26]">acquired</span>.</h2>
          <button onClick={() => setPage('collection')} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B2D26] hover:text-[#0E0E0E] flex items-center gap-1 group">View all <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></button>
        </div>
        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="group cursor-pointer">
            <div className="aspect-[4/5] bg-[#F8F2E2] overflow-hidden p-12 border border-[#0E0E0E]/8"><img src={FEATURED[1].img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" /></div>
            <div className="mt-6 flex items-end justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7B2D26]">{FEATURED[1].brand} &middot; Ref. {FEATURED[1].ref} &middot; {FEATURED[1].year}</p>
                <h3 className="font-serif text-3xl mt-2" dangerouslySetInnerHTML={{ __html: FEATURED[1].name }} />
                <p className="font-mono text-[11px] text-[#0E0E0E]/55 mt-3">{FEATURED[1].case}</p>
                <p className="font-mono text-[11px] text-[#0E0E0E]/55" dangerouslySetInnerHTML={{ __html: FEATURED[1].cond }} />
              </div>
              <p className="font-serif text-2xl text-[#7B2D26] whitespace-nowrap">CHF {FEATURED[1].price}</p>
            </div>
          </motion.div>
          <div className="space-y-8">
            {[FEATURED[0], FEATURED[2]].map((w, i) => (
              <motion.div key={w.ref} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group cursor-pointer grid grid-cols-[160px_1fr] gap-6 items-start border-b border-[#0E0E0E]/10 pb-6 last:border-b-0">
                <div className="aspect-square bg-[#F8F2E2] overflow-hidden p-4 border border-[#0E0E0E]/8"><img src={w.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" /></div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7B2D26]">{w.brand} &middot; {w.year}</p>
                  <h3 className="font-serif text-xl mt-1.5 leading-tight" dangerouslySetInnerHTML={{ __html: w.name }} />
                  <p className="font-mono text-[11px] text-[#0E0E0E]/55 mt-2">{w.case}</p>
                  <p className="font-serif text-lg text-[#7B2D26] mt-3">CHF {w.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline — horizontal, scrollable on mobile */}
      <section className="bg-[#0E0E0E] text-[#F2EBDA] py-24">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#A88B4F] mb-3">— A history of fifty years</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-tight mb-16">The same street. <span className="italic text-[#A88B4F]">The same bench</span>.</h2>
          <div className="relative">
            <div className="absolute top-[18px] left-0 right-0 h-px bg-[#F2EBDA]/15" />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-4 relative">
              {TIMELINE.map((t, i) => (
                <motion.div key={t.year} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative">
                  <div className="w-3 h-3 rounded-full bg-[#A88B4F] mb-6 mx-0" />
                  <p className="font-serif text-3xl text-[#A88B4F]">{t.year}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase mt-2 mb-3">{t.t}</p>
                  <p className="text-sm text-[#F2EBDA]/55 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.d }} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-24 max-w-[900px] mx-auto px-8 lg:px-12">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-6">— Henrik Vermaelen, founder</p>
        <blockquote className="font-serif text-3xl lg:text-5xl leading-[1.2] italic">&ldquo;A watch is a contract with time. Our work is to keep it honest.&rdquo;</blockquote>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#0E0E0E]/45 mt-8">Workbench notes, 2018 &middot; <a href="#" className="text-[#7B2D26] hover:underline">Read the full essay →</a></p>
      </section>
    </div>
  );
}
